/**
 * @module game/Spawner
 * Controls fruit, bomb, and power-up spawn timing, trajectory, and
 * difficulty scaling.
 *
 * Difficulty scaling (Classic mode):
 *  - Every CONFIG.game.difficultyTickMs the effective spawn interval shrinks
 *    by CONFIG.game.spawnIntervalDelta, floored at CONFIG.game.minSpawnInterval.
 *  - Launch speed grows by CONFIG.game.speedDelta per tick, capped at
 *    CONFIG.game.maxSpeed.
 *
 * Frenzy mode: the effective interval is divided by 4, flooding the screen.
 *
 * Bomb spawn: only in Classic mode, using CONFIG.bomb.spawnChance.
 */

import { CONFIG } from '../game-config.js';
import type { FruitType } from '../game-config.js';

/** Fruit types from the canonical CONFIG — keys of CONFIG.fruits. */
const FRUIT_TYPES: FruitType[] = Object.keys(CONFIG.fruits) as FruitType[];

/** The three entity kinds the spawner can produce. */
export type SpawnKind = 'fruit' | 'bomb' | 'powerup';

/** A single spawn request returned by {@link Spawner.tick}. */
export interface SpawnRequest {
  kind: SpawnKind;
  /** Set when kind === 'fruit'. */
  fruitType?: FruitType;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** True for rare critical (golden) fruit. */
  isCritical: boolean;
}

/** Canvas dimensions snapshot passed to each tick. */
export interface SpawnContext {
  canvasWidth: number;
  canvasHeight: number;
}

/**
 * Stateful spawner — accumulates frame deltas and emits spawn requests
 * once sufficient time has elapsed.
 *
 * One instance per game session; call {@link Spawner.reset} on restart.
 */
export class Spawner {
  /** Accumulated time waiting for next spawn (ms). */
  private _accumulator = 0;
  /** Total elapsed game time (ms) — drives difficulty scaling. */
  private _elapsed = 0;
  /** Current effective spawn interval (ms), shrinks with difficulty. */
  private _interval = CONFIG.fruit.spawnInterval;
  /** Current effective launch speed (px/s), grows with difficulty. */
  private _speed = CONFIG.fruit.baseSpeed;
  /** Elapsed time since last difficulty escalation (ms). */
  private _diffTick = 0;

  /**
   * Advances the spawner by `deltaMs` and returns any entities to create.
   *
   * @param deltaMs - Frame delta in milliseconds.
   * @param ctx - Canvas size (used to position spawn X and Y).
   * @param isFrenzy - Whether a frenzy wave is active.
   * @param isClassic - Whether the current mode is Classic (enables bombs).
   */
  tick(
    deltaMs: number,
    ctx: SpawnContext,
    isFrenzy: boolean,
    isClassic: boolean,
  ): SpawnRequest[] {
    this._elapsed += deltaMs;
    this._accumulator += deltaMs;
    this._diffTick += deltaMs;

    // Difficulty escalation.
    if (this._diffTick >= CONFIG.game.difficultyTickMs) {
      this._diffTick -= CONFIG.game.difficultyTickMs;
      this._interval = Math.max(
        CONFIG.game.minSpawnInterval,
        this._interval - CONFIG.game.spawnIntervalDelta,
      );
      this._speed = Math.min(
        CONFIG.game.maxSpeed,
        this._speed + CONFIG.game.speedDelta,
      );
    }

    const effectiveInterval = isFrenzy ? this._interval / 4 : this._interval;
    const requests: SpawnRequest[] = [];

    while (this._accumulator >= effectiveInterval) {
      this._accumulator -= effectiveInterval;
      requests.push(this._buildRequest(ctx, isClassic));
    }

    return requests;
  }

  /**
   * Emits exactly `count` spawn requests immediately, bypassing the accumulator.
   * Used for frenzy wave bursts where the FrenzyManager dictates the fruit count.
   */
  spawnBurst(count: number, ctx: SpawnContext, isClassic: boolean): SpawnRequest[] {
    const requests: SpawnRequest[] = [];
    for (let i = 0; i < count; i++) {
      requests.push(this._buildRequest(ctx, isClassic));
    }
    return requests;
  }

  /** Returns the current effective spawn interval (ms) — useful for tests. */
  get currentInterval(): number {
    return this._interval;
  }

  /** Returns the current effective launch speed (px/s) — useful for tests. */
  get currentSpeed(): number {
    return this._speed;
  }

  /** Resets all state for a new game session. */
  reset(): void {
    this._accumulator = 0;
    this._elapsed = 0;
    this._interval = CONFIG.fruit.spawnInterval;
    this._speed = CONFIG.fruit.baseSpeed;
    this._diffTick = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _buildRequest(ctx: SpawnContext, isClassic: boolean): SpawnRequest {
    // Spawn along the bottom within the configured horizontal band.
    const spawnBandWidth =
      (CONFIG.fruit.spawnXMax - CONFIG.fruit.spawnXMin) * ctx.canvasWidth;
    const x =
      CONFIG.fruit.spawnXMin * ctx.canvasWidth +
      Math.random() * spawnBandWidth;
    const y = ctx.canvasHeight + 60; // just below the visible area

    // Random launch angle within the configured range — drives horizontal
    // drift only. Difficulty-scaled `_speed` controls how fast that drift is.
    const angleDeg =
      CONFIG.fruit.launchAngleMin +
      Math.random() * (CONFIG.fruit.launchAngleMax - CONFIG.fruit.launchAngleMin);
    const mirrorX = Math.random() < 0.5;
    const rad = (angleDeg * Math.PI) / 180;
    const vx = Math.cos(rad) * this._speed * (mirrorX ? -1 : 1);

    // Vertical speed targets a peak at a fraction of the canvas height so
    // fruits are always reachable regardless of viewport size (v = √(2·g·rise)).
    // A fixed launch speed would peak ~265px above the bottom on every screen,
    // leaving most of a desktop viewport without gameplay.
    const riseFrac =
      CONFIG.fruit.peakHeightMin +
      Math.random() * (CONFIG.fruit.peakHeightMax - CONFIG.fruit.peakHeightMin);
    const rise = ctx.canvasHeight * riseFrac + 60; // +60 clears the below-screen spawn offset
    const vy = -Math.sqrt(2 * CONFIG.fruit.gravity * rise);

    // Roll for spawn kind.
    const roll = Math.random();
    let cumulative = CONFIG.powerup.spawnChance;

    if (roll < cumulative) {
      return { kind: 'powerup', x, y, vx, vy, isCritical: false };
    }

    if (isClassic) {
      cumulative += CONFIG.bomb.spawnChance;
      if (roll < cumulative) {
        return { kind: 'bomb', x, y, vx, vy, isCritical: false };
      }
    }

    // Fruit spawn.
    const isCritical = Math.random() < CONFIG.fruit.criticalChance;
    const fruitType =
      FRUIT_TYPES[Math.floor(Math.random() * FRUIT_TYPES.length)] ?? 'apel';

    return { kind: 'fruit', fruitType, x, y, vx, vy, isCritical };
  }
}
